<template>
    <v-container fluid class="team-page-container px-0">
        <div
            class="team-sticky-header"
            :style="stickyHeaderStyle"
            :class="{
                'team-sticky-header-desktop': isDesktopHeaderEnabled,
                'team-header-compact': isDesktopHeaderEnabled && isHeaderCompact
            }"
        >
            <team-banner
                :team="team"
                :type="type"
                :ranking="ranking"
                :phase-counts="phaseCounts"
                :compact="isDesktopHeaderEnabled && isHeaderCompact"
                @setType="setType"
            />
            <v-row v-if="!$vuetify.breakpoint.mobile" class="team-tabs-row">
                <v-col cols="12" class="mx-0 px-0">
                    <v-tabs
                        v-model="tab"
                        centered
                        class="team-tabs"
                        :class="{ 'team-tabs-compact': isDesktopHeaderEnabled && isHeaderCompact }"
                    >
                        <v-tabs-slider class="custom-orange" />
                        <v-tab v-for="(item, index) in items" :key="index">{{ item.name }}</v-tab>
                    </v-tabs>
                </v-col>
            </v-row>
        </div>
        <!-- Mobile: app-style segmented control, sticky under the app header -->
        <nav v-if="$vuetify.breakpoint.mobile" class="team-segments">
            <div class="team-segments__track">
                <button
                    v-for="(item, index) in items"
                    :key="index"
                    type="button"
                    class="team-segments__btn"
                    :class="{ 'team-segments__btn--active': tab === index }"
                    @click="tab = index"
                >
                    <v-icon small>{{ item.icon }}</v-icon>
                    <span>{{ item.name }}</span>
                </button>
            </div>
        </nav>
        <div class="team-content-wrapper">
            <team-standings
                v-if="tab === 0"
                :ranking="ranking"
                :matches="standingsMatches"
                :team-matches="matches"
                :type="type"
                :team-id="team.id || $route.params.id"
                :season-id="getCurrentSeasonId()"
                :league-id="getCurrentLeagueId()"
                @show-all-matches="tab = 2"
            />
            <roster v-else-if="tab === 1" :players="team.players || []" :color="team.color" />
            <schedule-match-center v-else-if="tab === 2" :matches="matches" />
            <team-stats v-else-if="tab === 3" :players="team.players || []" :matches-played="(team.seasonLoses || 0) + (team.seasonWins || 0)" :type="type" />
        </div>
    </v-container>
</template>

<script>
    import TeamBanner from '@/components/TeamBanner'
    import Roster from '@/components/Roster'
    import ScheduleMatchCenter from '@/components/ScheduleMatchCenter'
    import TeamStats from '@/components/TeamStats'
    import teamService from '@/services/team'
    import leagueService from '@/services/league'
    import TeamStandings from '@/components/TeamStandings'
    import matchService from '@/services/match'
    import { isMatchLive, refreshLiveStatuses, pollWhileVisible, SCHEDULE_REFRESH_MS } from '@/utils/matchLive'
    import { foulsOf } from '@/utils/playerStats'

    export default {
        components: { TeamBanner, Roster, ScheduleMatchCenter, TeamStats, TeamStandings },
        data() {
            return {
                tab: 0,
                assetsUrl: 'https://storage.googleapis.com',
                items: [
                    {
                        name: 'Κεντρική',
                        icon: 'mdi-home-outline'
                    },
                    {
                        name: 'Ρόστερ',
                        icon: 'mdi-account-group-outline'
                    },
                    {
                        name: 'Παιχνίδια',
                        icon: 'mdi-calendar-month-outline'
                    },
                    {
                        name: 'Στατιστικά',
                        icon: 'mdi-chart-box-outline'
                    }
                ],
                team: {},
                ranking: [],
                matches: [],
                standingsMatches: [],
                // this season's matches per phase, for the hints on the phase buttons
                phaseMatches: {},
                now: Date.now(),
                nowTimer: null,
                stopMatchesRefresh: null,
                type: 'regular_season',
                matchTypes: ['placements', 'regular_season', 'playoffs', 'cup'],
                activePhasePresetApplied: false,
                seasonId: null,
                isHeaderCompact: false,
                compactEnterThreshold: 20,
                compactExitThreshold: 2,
                scrollRafId: null,
                headerToggleCooldownMs: 350,
                headerToggleLockedUntil: 0
            }
        },
        computed: {
            // Upcoming and live matches per phase (finished ones are not counted)
            phaseCounts() {
                const counts = {}
                Object.keys(this.phaseMatches).forEach(phase => {
                    const matches = this.phaseMatches[phase]
                    const live = matches.filter(match => isMatchLive(match, this.now)).length
                    const upcoming = matches.filter(match => match.pending && !match.postponed && !isMatchLive(match, this.now)).length
                    counts[phase] = { upcoming, live }
                })
                return counts
            },
            isDesktopHeaderEnabled() {
                return !this.$vuetify.breakpoint.mobile
            },
            stickyHeaderStyle() {
                if (!this.isDesktopHeaderEnabled) {
                    return {}
                }
                return {
                    top: '60px'
                }
            },
            activePhase() {
                const config = this.$store.getters.getConfig
                return config && config.activePhase ? config.activePhase : null
            }
        },
        watch: {
            '$route.params.id': {
                handler() {
                    this.applyTabFromRoute()
                    this.fetchPhaseCounts()
                    this.getTeam(this.$route.params.id)
                    this.getRanking(this.$route.params.id)
                    this.fetchMatchesByTeam()
                }
            },
            '$route.query.tab': {
                handler() {
                    this.applyTabFromRoute()
                }
            },
            tab: {
                handler(newTab) {
                    this.syncTabToRoute(newTab)
                }
            },
            type: {
                handler() {
                    this.getTeam(this.$route.params.id)
                    this.getRanking(this.$route.params.id)
                    this.fetchMatchesByTeam()
                }
            },
            '$vuetify.breakpoint.mobile': {
                handler(isMobile) {
                    if (isMobile) {
                        this.isHeaderCompact = false
                    } else {
                        this.handleHeaderScroll()
                    }
                }
            },
            activePhase: {
                handler() {
                    this.applyActivePhasePreset()
                }
            }
        },
        created() {
            this.applyTabFromRoute()
            this.fetchPhaseCounts()
            const presetApplied = this.applyActivePhasePreset()
            if (!presetApplied) {
                this.getTeam(this.$route.params.id)
                this.getRanking(this.$route.params.id)
                this.fetchMatchesByTeam()
            }
        },
        mounted() {
            // keeps the live / upcoming hints of the phase buttons current
            this.nowTimer = setInterval(() => {
                this.now = Date.now()
                refreshLiveStatuses([].concat(...Object.values(this.phaseMatches)), this.now)
            }, 60 * 1000)
            // new times, postponements and final results without a reload
            this.stopMatchesRefresh = pollWhileVisible(() => {
                this.refreshMatches()
                this.fetchPhaseCounts()
            }, SCHEDULE_REFRESH_MS)
            this.handleHeaderScroll()
            window.addEventListener('scroll', this.onScroll, { passive: true })
        },
        beforeDestroy() {
            clearInterval(this.nowTimer)
            this.stopMatchesRefresh()
            window.removeEventListener('scroll', this.onScroll)
            if (this.scrollRafId) {
                window.cancelAnimationFrame(this.scrollRafId)
                this.scrollRafId = null
            }
        },
        methods: {
            applyActivePhasePreset() {
                if (this.activePhasePresetApplied) {
                    return false
                }

                if (!this.activePhase || !this.matchTypes.includes(this.activePhase) || this.type !== 'regular_season') {
                    return false
                }

                this.activePhasePresetApplied = true

                if (this.activePhase === this.type) {
                    return false
                }

                this.type = this.activePhase
                return true
            },
            setHeaderCompact(nextValue) {
                if (this.isHeaderCompact === nextValue) {
                    return
                }

                const now = Date.now()
                if (now < this.headerToggleLockedUntil) {
                    return
                }

                this.isHeaderCompact = nextValue
                this.headerToggleLockedUntil = now + this.headerToggleCooldownMs
            },
            onScroll() {
                if (this.scrollRafId) {
                    return
                }
                this.scrollRafId = window.requestAnimationFrame(() => {
                    this.scrollRafId = null
                    this.handleHeaderScroll()
                })
            },
            handleHeaderScroll() {
                const scrollY = Math.max(0, window.scrollY || window.pageYOffset || 0)

                if (!this.isDesktopHeaderEnabled) {
                    this.isHeaderCompact = false
                    return
                }

                if (scrollY <= this.compactExitThreshold) {
                    this.setHeaderCompact(false)
                } else if (scrollY >= this.compactEnterThreshold) {
                    this.setHeaderCompact(true)
                }
            },
            parseNumeric(value) {
                const parsed = Number.parseInt(value, 10)
                return Number.isNaN(parsed) ? null : parsed
            },
            getRouteSeasonId() {
                return this.parseNumeric(this.$route?.query?.seasonId)
            },
            getRouteLeagueId() {
                const routeLeague = this.$route?.query?.leagueId
                if (routeLeague === 'Cup') return null
                return this.parseNumeric(routeLeague)
            },
            getMatchesSeasonId() {
                const source = Array.isArray(this.matches) && this.matches.length ? this.matches[0] : null
                return source?.seasonId || source?.season?.id || null
            },
            getMatchesLeagueId() {
                const source = Array.isArray(this.matches) && this.matches.length ? this.matches[0] : null
                return source?.league?.id || source?.leagueId || null
            },
            getRouteTab() {
                const parsedTab = Number.parseInt(this.$route.query.tab, 10)
                if (Number.isNaN(parsedTab) || parsedTab < 0 || parsedTab >= this.items.length) {
                    return 0
                }
                return parsedTab
            },
            applyTabFromRoute() {
                const routeTab = this.getRouteTab()
                if (this.tab !== routeTab) {
                    this.tab = routeTab
                }
            },
            syncTabToRoute(tab) {
                if (tab === null || tab === undefined) {
                    return
                }
                const routeTab = this.getRouteTab()
                if (tab === routeTab && this.$route.query.tab !== undefined) {
                    return
                }
                this.$router.replace({
                    query: {
                        ...this.$route.query,
                        tab: String(tab)
                    }
                }).catch(() => {})
            },
            getMatchesPlayed() {
                if (this.type === 'cup') {
                    this.team.seasonLoses = 0
                    this.team.seasonWins = 0
                    this.matches.forEach(element => {
                        if (element.team1.id === parseInt(this.$route.params.id) && element.scoreTeam1 > element.scoreTeam2) {
                            this.team.seasonWins += 1
                        } else if (element.team1.id === parseInt(this.$route.params.id) && element.scoreTeam1 < element.scoreTeam2) {
                            this.team.seasonLoses += 1
                        } else if (element.team2.id === parseInt(this.$route.params.id) && element.scoreTeam2 > element.scoreTeam1) {
                            this.team.seasonWins += 1
                        } else if (element.team2.id === parseInt(this.$route.params.id) && element.scoreTeam2 < element.scoreTeam1) {
                            this.team.seasonLoses += 1
                        }
                    })
                }
            },
            setType(option) {
                if (typeof option === 'string' && this.matchTypes.includes(option)) {
                    if (this.type === option) {
                        return
                    }
                    this.type = option
                    return
                }

                if (typeof option === 'number' && this.matchTypes[option]) {
                    const nextType = this.matchTypes[option]
                    if (this.type === nextType) {
                        return
                    }
                    this.type = nextType
                }
            },
            getTeam(teamId) {
                teamService.getTeam(teamId, this.type).then(resp => {
                    this.team = resp.data
                    this.seasonId = this.team?.seasonId || this.team?.season?.id || this.seasonId
                    this.setTeamPageMeta(this.team)
                    this.calculateTeamStats()
                    this.getMatchesPlayed()
                    this.fetchStandingsMatches()
                })
            },
            upsertMetaTag(selector, attrs) {
                let el = document.head.querySelector(selector)
                if (!el) {
                    el = document.createElement('meta')
                    document.head.appendChild(el)
                }
                Object.keys(attrs).forEach(key => {
                    el.setAttribute(key, attrs[key])
                })
            },
            getTeamLogoMetaUrl(team = {}) {
                const raw = team.logo || team.image || team.badge || null
                if (!raw) return `${window.location.origin}/icon-512.png`
                if (/^https?:\/\//i.test(raw)) return raw
                return `${this.assetsUrl}/${raw}`
            },
            setTeamPageMeta(team = {}) {
                const siteName = 'BIG Basketball'
                const teamName = String(team.name || 'Ομάδα').trim()
                const leagueTitle = String(team?.league?.title || team.leagueTitle || '').trim()
                const title = leagueTitle
                    ? `${teamName} | ${leagueTitle} | ${siteName}`
                    : `${teamName} | ${siteName}`
                const description = leagueTitle
                    ? `Δείτε την ομάδα ${teamName} στη διοργάνωση ${leagueTitle}: ρόστερ, αγώνες και στατιστικά.`
                    : `Δείτε την ομάδα ${teamName}: ρόστερ, αγώνες και στατιστικά.`
                const image = this.getTeamLogoMetaUrl(team)
                const url = window.location.href

                document.title = title

                this.upsertMetaTag('meta[name="description"]', { name: 'description', content: description })
                this.upsertMetaTag('meta[property="og:site_name"]', { property: 'og:site_name', content: siteName })
                this.upsertMetaTag('meta[property="og:title"]', { property: 'og:title', content: title })
                this.upsertMetaTag('meta[property="og:description"]', { property: 'og:description', content: description })
                this.upsertMetaTag('meta[property="og:image"]', { property: 'og:image', content: image })
                this.upsertMetaTag('meta[property="og:url"]', { property: 'og:url', content: url })
                this.upsertMetaTag('meta[property="og:type"]', { property: 'og:type', content: 'website' })
                this.upsertMetaTag('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary' })
                this.upsertMetaTag('meta[name="twitter:title"]', { name: 'twitter:title', content: title })
                this.upsertMetaTag('meta[name="twitter:description"]', { name: 'twitter:description', content: description })
                this.upsertMetaTag('meta[name="twitter:image"]', { name: 'twitter:image', content: image })
            },
            getRanking(teamId) {
                if (this.type === 'playoffs') {
                    const seasonId = this.getCurrentSeasonId()
                    const leagueId = this.getCurrentLeagueId()

                    if (seasonId && leagueId) {
                        leagueService.getRankingByLeague(leagueId, seasonId, 'regular_season').then(resp => {
                            this.ranking = resp.data
                        }).catch(() => {
                            teamService.getRankingByTeam(teamId, this.type).then(resp => {
                                this.ranking = resp.data
                            })
                        })
                        return
                    }
                }

                teamService.getRankingByTeam(teamId, this.type).then(resp => {
                    this.ranking = resp.data
                })
            },
            calculateTeamStats() {
                (this.team.players || []).forEach(player => {
                    player.avg = this.getAverageStats(player)
                    player.cum = this.getCummulativeStats(player)
                })
            },
            getCummulativeStats(player) {
                const stats = {
                    points: 0,
                    rebounds: 0,
                    assists: 0,
                    assistPoints: 0,
                    secondChancePoints: 0,
                    pointsOffTurnovers: 0,
                    steals: 0,
                    turnovers: 0,
                    blockedShots: 0,
                    twoPointsMade: 0,
                    twoPointAttempts: 0,
                    threePointsMade: 0,
                    threePointAttempts: 0,
                    freeThrowsMade: 0,
                    freeThrowAttempts: 0,
                    offensiveRebounds: 0,
                    defensiveRebounds: 0,
                    personalFouls: 0,
                    fouls: 0,
                    pir: 0,
                    playTimeInSecs: 0
                }
                if (this.type === 'cup') {
                    player.stats = player.stats.filter(element => element.cup)
                }
                player.stats.forEach(element => {
                    for (const key of Object.keys(stats)) {
                        stats[key] += key === 'fouls' ? foulsOf(element) : element[key]
                    }
                })
                return stats
            },
            getAverageStats(player) {
                const stats = {
                    points: 0,
                    rebounds: 0,
                    assists: 0,
                    assistPoints: 0,
                    secondChancePoints: 0,
                    pointsOffTurnovers: 0,
                    steals: 0,
                    turnovers: 0,
                    blockedShots: 0,
                    twoPointsMade: 0,
                    twoPointAttempts: 0,
                    threePointsMade: 0,
                    threePointAttempts: 0,
                    freeThrowsMade: 0,
                    freeThrowAttempts: 0,
                    offensiveRebounds: 0,
                    defensiveRebounds: 0,
                    personalFouls: 0,
                    fouls: 0,
                    pir: 0,
                    playTimeInSecs: 0
                }
                if (this.type === 'cup') {
                    player.stats = player.stats.filter(element => element.cup)
                }
                player.stats.forEach(element => {
                    for (const key of Object.keys(stats)) {
                        stats[key] += key === 'fouls' ? foulsOf(element) : element[key]
                    }
                })
                Object.keys(stats).forEach(element => {
                    if (player.stats.length) {
                        stats[element] = stats[element] / player.stats.length
                    } else {
                        stats[element] = 0
                    }
                })
                return stats
            },
            fetchPhaseCounts() {
                const teamId = this.$route.params.id
                // one call per phase, the same way the page loads the selected phase;
                // the endpoint answers MATCH_NOT_FOUND when a phase has no matches
                Promise.all(this.matchTypes.map(phase => matchService.getMatchesByTeam(teamId, null, phase)
                    .then(resp => resp.data || [])
                    .catch(() => [])
                )).then(results => {
                    if (String(teamId) !== String(this.$route.params.id)) {
                        return
                    }
                    const phaseMatches = {}
                    this.matchTypes.forEach((phase, index) => {
                        phaseMatches[phase] = results[index]
                    })
                    this.phaseMatches = phaseMatches
                    refreshLiveStatuses([].concat(...results))
                })
            },
            fetchMatchesByTeam() {
                matchService.getMatchesByTeam(this.$route.params.id, null, this.type).then(resp => {
                    this.matches = resp.data
                    this.getMatchesSorted(this.matches)
                    this.fetchStandingsMatches()
                    this.getRanking(this.$route.params.id)
                }).catch(error => {
                    if (error.response.data.code === 'MATCH_NOT_FOUND') {
                        this.matches = []
                        this.standingsMatches = []
                    }
                })
            },
            // Background reload of the phase on screen; the list stays as it is if the call fails
            refreshMatches() {
                const teamId = this.$route.params.id
                const type = this.type
                matchService.getMatchesByTeam(teamId, null, type).then(resp => {
                    if (String(teamId) !== String(this.$route.params.id) || type !== this.type) {
                        return
                    }
                    this.matches = resp.data
                    this.getMatchesSorted(this.matches)
                    this.fetchStandingsMatches()
                }).catch(() => {})
            },
            getCurrentSeasonId() {
                return this.team?.seasonId ||
                    this.team?.season?.id ||
                    this.getMatchesSeasonId() ||
                    this.getRouteSeasonId() ||
                    this.seasonId ||
                    null
            },
            getCurrentLeagueId() {
                return this.team?.league?.id ||
                    this.team?.leagueId ||
                    this.getMatchesLeagueId() ||
                    this.getRouteLeagueId() ||
                    null
            },
            fetchStandingsMatches() {
                this.standingsMatches = [...this.matches]
            },
            getMatchesSorted(matches = this.matches) {
                matches.sort(function(a, b) {
                    if (a.round > b.round) {
                        return 1
                    } else {
                        return -1
                    }
                })
            }
        }
    }
</script>

<style scoped>
.team-page-container {
    position: relative;
    padding-left: 0 !important;
    padding-right: 0 !important;
    overflow-anchor: none;
}

.team-sticky-header {
    position: relative;
    width: 100%;
    overflow-anchor: none;
}

.team-sticky-header-desktop {
    position: sticky;
    z-index: 9;
    background: transparent;
    backdrop-filter: none;
    border-radius: 0;
    transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.28s ease;
}

.team-header-compact {
    box-shadow: none;
}

.team-tabs-row {
    margin-top: 0;
    padding: 0;
    transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.22s ease;
}

.team-header-compact .team-tabs-row {
    transform: translateY(-1px);
}

.team-tabs {
    max-width: 1200px;
    margin: 8px auto 0;
    background: transparent;
}

.team-tabs ::v-deep .v-tabs-bar {
    border-radius: 10px;
    background: #fff !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* ---------- Mobile segmented tabs ---------- */
.team-segments {
    /* split in two: the build's CSS minifier cannot parse max() nested in calc() */
    --safe-top: max(env(safe-area-inset-top), var(--safe-area-inset-top, env(safe-area-inset-top)));

    position: sticky;
    top: calc(56px + var(--safe-top));
    z-index: 5;
    padding: 10px 12px 6px;
}

.team-segments__track {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 4px;
    padding: 4px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(10px);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.team-segments__btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
    min-width: 0;
    height: 48px;
    border-radius: 10px;
    color: #6b6b70;
    font-family: Ubuntu, Arial, sans-serif;
    font-size: 0.72rem;
    font-weight: 700;
    -webkit-tap-highlight-color: transparent;
    transition: background 0.2s ease, color 0.2s ease;
}

.team-segments__btn span {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.team-segments__btn .v-icon {
    color: inherit !important;
}

.team-segments__btn--active {
    background: linear-gradient(90deg, #ef4444 0%, #ff7133 50%, orange 100%);
    color: #fff;
    box-shadow: 0 3px 8px rgba(239, 68, 68, 0.3);
}

/* Same width as the rest of the site */
.team-content-wrapper {
    max-width: 1224px;
    padding: 0 12px 16px;
    margin: 16px auto 0;
}

.v-tab {
    text-transform: none !important;
    font-weight: bolder;
    font-size: 15px;
    color: #757578 !important;
}

.v-tab::before {
    opacity: 0;
    pointer-events: none;
    position: absolute;
    background-color: transparent;
}

.v-tab--active {
    background: linear-gradient(90deg, #ef4444 0%, #ff7133 70%, orange 100%);
    background-clip: text;
    -webkit-text-fill-color: transparent;
}
</style>
